import React, { useState, useEffect, useRef } from 'react';
import { Popover, PopoverBody } from 'reactstrap';

const ShortcutHandler = ({
  textareaRef,
  message,
  setMessage,
  cursorPosition,
  templates = [],
  onCommandSelect,
  onClose
}) => {
  const [filteredCommands, setFilteredCommands] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [commandQuery, setCommandQuery] = useState('');
  const [showPopover, setShowPopover] = useState(false);

  const baseCommands = [
    { id: 'template', label: 'template', description: 'Insert a canned response', icon: '📝' },
    { id: 'attach', label: 'attach', description: 'Attach a file', icon: '📎' },
  ];

  useEffect(() => {
    if (!message || cursorPosition === null) {
      setShowPopover(false);
      setFilteredCommands([]);
      return;
    }

    // Find / command at cursor position
    const textBeforeCursor = message.substring(0, cursorPosition);
    const lastSlashIndex = textBeforeCursor.lastIndexOf('/');
    
    if (lastSlashIndex === -1) {
      setShowPopover(false);
      setFilteredCommands([]);
      return;
    }

    // Check if / is at start of line or after whitespace
    const textBeforeSlash = textBeforeCursor.substring(0, lastSlashIndex);
    const isAtStart = lastSlashIndex === 0;
    const isAfterWhitespace = /\s$/.test(textBeforeSlash);
    
    if (!isAtStart && !isAfterWhitespace) {
      setShowPopover(false);
      setFilteredCommands([]);
      return;
    }

    // Extract query after /
    const query = textBeforeCursor.substring(lastSlashIndex + 1);
    const hasSpace = /\s/.test(query);
    
    if (hasSpace) {
      setShowPopover(false);
      setFilteredCommands([]);
      return;
    }

    setCommandQuery(query.toLowerCase());
    setShowPopover(true);
    
    // Combine base commands with template shortcuts
    const allCommands = [
      ...baseCommands,
      ...templates
        .filter(t => t.shortcut)
        .map(t => ({
          id: `template-${t.id}`,
          label: t.shortcut,
          description: t.title || 'Template',
          icon: '📋',
          template: t
        }))
    ];

    // Filter commands
    const filtered = allCommands
      .filter(cmd => {
        const searchTerm = query.toLowerCase();
        return cmd.label.toLowerCase().includes(searchTerm) || 
               cmd.description.toLowerCase().includes(searchTerm);
      })
      .slice(0, 10); // Limit to 10 results

    setFilteredCommands(filtered);
    setSelectedIndex(0);
  }, [message, cursorPosition, templates]);

  const executeCommand = (command) => {
    if (!textareaRef.current) return;

    // Find / position
    const textBeforeCursor = message.substring(0, cursorPosition);
    const lastSlashIndex = textBeforeCursor.lastIndexOf('/');
    
    if (lastSlashIndex === -1) return;

    const beforeCommand = message.substring(0, lastSlashIndex);
    const afterCursor = message.substring(cursorPosition);

    if (command.id === 'schedule') {
      // Trigger schedule modal
      if (onCommandSelect) {
        onCommandSelect('schedule');
      }
      // Remove the /schedule text
      setMessage(`${beforeCommand}${afterCursor}`);
    } else if (command.id === 'attach') {
      // Trigger file upload
      if (onCommandSelect) {
        onCommandSelect('attach');
      }
      // Remove the /attach text
      setMessage(`${beforeCommand}${afterCursor}`);
    } else if (command.id === 'template' || command.id === 'canned') {
      // Open template picker
      if (onCommandSelect) {
        onCommandSelect('template');
      }
      // Remove the /template or /canned text
      setMessage(`${beforeCommand}${afterCursor}`);
    } else if (command.template) {
      // Insert template content
      const template = command.template;
      const separator = beforeCommand && !beforeCommand.endsWith('\n') ? '\n\n' : '';
      const newMessage = `${beforeCommand}${separator}${template.content}${afterCursor}`;
      setMessage(newMessage);
      
      // Set cursor position after template
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = beforeCommand.length + separator.length + template.content.length;
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          textareaRef.current.focus();
        }
      }, 0);
    }

    if (onClose) onClose();
  };

  const handleKeyDown = (e) => {
    if (!showPopover || filteredCommands.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % filteredCommands.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + filteredCommands.length) % filteredCommands.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      if (filteredCommands[selectedIndex]) {
        executeCommand(filteredCommands[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowPopover(false);
      if (onClose) onClose();
    }
  };

  useEffect(() => {
    if (showPopover && textareaRef.current) {
      textareaRef.current.addEventListener('keydown', handleKeyDown);
      return () => {
        textareaRef.current?.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [showPopover, filteredCommands, selectedIndex]);

  if (!showPopover || filteredCommands.length === 0) return null;

  // Calculate popover position (this is a simplified version)
  // In a real implementation, you'd want to calculate the exact position based on cursor
  return (
    <div
      className="shortcut-popover"
      style={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        marginBottom: '8px',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
        zIndex: 1000,
        minWidth: '280px',
        maxWidth: '360px',
        maxHeight: '300px',
        overflowY: 'auto'
      }}
    >
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>
        Commands
      </div>
      {filteredCommands.map((command, index) => {
        const isSelected = index === selectedIndex;
        
        return (
          <div
            key={command.id}
            onClick={() => executeCommand(command)}
            style={{
              padding: '10px 12px',
              cursor: 'pointer',
              backgroundColor: isSelected ? '#f3f4f6' : 'transparent',
              borderLeft: isSelected ? '3px solid #2563eb' : '3px solid transparent',
              transition: 'background-color 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <span style={{ fontSize: '18px' }}>{command.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#1f2937', marginBottom: '2px' }}>
                /{command.label}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {command.description}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ShortcutHandler;

